import { Stack } from '@mui/material'
import { TableCellProps } from '@mui/material/TableCell/TableCell'
import { roundingNumber } from 'components/shared/processes'
import { Fatness } from 'models/IFarm'
import { CustomTableRowProps } from 'ui/table/CustomTableRow'
import { TypographyPrimary } from 'ui/typography/TypographyPrimary'
import { COLORS } from 'styles/colors'

interface useGetCellOptionsProps {
	fatness?: Fatness[]
	totalCattle?: number
}

const getDeltaArrow = (item: Fatness) => {
	const previousValue = item.previous_value ?? item.prev_value
	if (previousValue == null || previousValue === item.value) return null

	const delta = item.value - previousValue
	const isNormal = item.label.trim().toLowerCase() === 'в норме'
	const isPositiveChange = isNormal ? delta > 0 : delta < 0

	return {
		symbol: delta > 0 ? '↑' : '↓',
		color: isPositiveChange ? COLORS.success : COLORS.error,
	}
}

export const useGetCellOptions = ({
	fatness,
	totalCattle = 0,
}: useGetCellOptionsProps) => {
	// если данных совсем нет — показываем фиксированный список категорий с "Нет данных"
	if (!fatness) {
		const DEFAULT_ROWS = [
			{ label: 'В норме', color: '#5222D0' },
			{ label: 'С отклонением', color: 'rgba(240, 154, 55, 1)' },
			{ label: 'С ожирением', color: '#EB4D3D' },
			{ label: 'С истощением', color: '#000000' },
		]
		return DEFAULT_ROWS.map(row => ({
			rowKey: 'tableFatnessWidgetRow',
			rowHeight: '18px',
			cellItems: [
				{
					padding: '4px 0px 0px 0px',
					children: (
						<Stack direction='row' alignItems='center' gap='4px'>
							<span
								style={{
									width: '8px',
									height: '8px',
									backgroundColor: row.color,
									borderRadius: '50%',
								}}
							></span>
							<TypographyPrimary>{row.label}</TypographyPrimary>
						</Stack>
					),
					align: 'left' as TableCellProps['align'],
				},
				{
					padding: '4px 0px 0px 0px',
					children: <TypographyPrimary>Нет данных</TypographyPrimary>,
					align: 'left' as TableCellProps['align'],
				},
				{
					padding: '4px 0px 0px 0px',
					children: '-',
					align: 'right' as TableCellProps['align'],
				},
			],
		}))
	}

	const TABLE_OPTIONS: CustomTableRowProps[] = fatness?.map(el => {
		const deltaArrow = getDeltaArrow(el)

		return {
			rowKey: 'tableFatnessWidgetRow',
			rowHeight: '18px',
			cellItems: [
				{
					padding: '4px 0px 0px 0px',
					children: (
						<Stack direction='row' alignItems='center' gap='4px'>
							<span
								style={{
									width: '8px',
									height: '8px',
									backgroundColor: el.color,
									borderRadius: '50%',
								}}
							></span>
							<TypographyPrimary>{el.label}</TypographyPrimary>
						</Stack>
					),
					align: 'left' as TableCellProps['align'],
				},
				{
					padding: '4px 0px 0px 0px',
					children: (
						<Stack direction='row' alignItems='center' gap='6px'>
							<TypographyPrimary>{el.value ?? '-'}</TypographyPrimary>
							{deltaArrow && (
								<TypographyPrimary
									sx={{ color: deltaArrow.color, lineHeight: 1 }}
								>
									{deltaArrow.symbol}
								</TypographyPrimary>
							)}
						</Stack>
					),
					align: 'left' as TableCellProps['align'],
				},
				{
					padding: '4px 0px 0px 0px',
					children:
						totalCattle === 0
							? '-'
							: roundingNumber((el.value / totalCattle) * 100, 0),
					align: 'right' as TableCellProps['align'],
				},
			],
		}
	})
	return TABLE_OPTIONS
}
